import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from '../../components/Select/Select';
import Button from '../../components/Button/Button';
import getMarks from '../../api/cars/getMarks';
import getCities from '../../api/resources/getCities';
import commitStockCorrections from '../../api/cars/commitStockCorrections';
import alert from '../../helpers/alert';
import useOriginOptions, { decomposeOrigin, originOptionFor } from './useOriginOptions';
import PendingRowForm from './PendingRowForm';
import ComplectationQuestions from './ComplectationQuestions';
import styles from './stockPendingRowsTable.module.css';

// Идентичность строки = файл + номер строки: в мультифайловой пачке один и тот
// же номер строки встречается в каждом файле, голый row не уникален. Этот ключ
// используется для всего локального состояния таблицы (выбор происхождения,
// раскрытие строк, блокировка кнопок) и как React key
const rowKey = (row) => `${row.fileName || ''}|${row.row}`;

// Строка полностью резолвлена бэком (марка/модель/город/ДЦ надёжно определены),
// не хватает только происхождения — оно не всегда есть в исходном файле (CRM-
// выгрузка клиента вообще не содержит это поле). Такие строки не должны
// заставлять пользователя заново разбирать полноценную форму — только выбрать
// происхождение и импортировать
const buildOriginOnlyResolution = (row, originOption) => ({
  fileName: row.fileName || null,
  row: row.row,
  markId: row.resolvedMarkId,
  modelId: row.resolvedModelId,
  complectationTitle: row.complectationName || '',
  cityId: row.resolvedCityId,
  dcId: row.resolvedDcId,
  vin: row.vin,
  color: row.color || '',
  engineVolume: row.engineVolume || '',
  engineType: row.engineType || null,
  transmission: row.transmission || null,
  priceStr: row.priceStr,
  issueYear: row.yearStr,
  // Менеджер уже подтвердил создание новой комплектации, строка ждала только происхождение
  createComplectation: !!row.createComplectation,
  ...decomposeOrigin(originOption),
});

// Одна строка вкладки "Ошибки" — вынесена в отдельный React.memo-компонент.
// На файле в тысячу ошибочных строк раньше вся эта разметка рендерилась инлайн
// внутри .map() у родителя: любое изменение состояния (клик по строке, выбор
// происхождения в ОДНОЙ строке) вызывало ре-рендер всех 1000+ строк разом,
// потому что колбэки/значения пересоздавались на каждый рендер и React не мог
// bail-out по shallow-equal пропсов. Плюс сама PendingRowForm (react-hook-form
// + несколько react-select) монтировалась для ВСЕХ строк сразу, просто скрытая
// через display:none — отсюда и медленный первый рендер.
// Тут форму монтируем только после первого разворачивания строки (и не
// размонтируем обратно — чтобы не терять то, что пользователь успел ввести).
const PendingErrorRow = React.memo(function PendingErrorRow({
  row,
  isExpanded,
  showFileNames,
  originOptions,
  originOption,
  allCities,
  allMarks,
  onToggle,
  onOriginChange,
  onSubmitRow,
  dcCache,
  createdModels,
  onModelCreated,
}) {
  const key = rowKey(row);
  const [hasOpened, setHasOpened] = useState(isExpanded);
  useEffect(() => {
    if (isExpanded) setHasOpened(true);
  }, [isExpanded]);

  return (
    <div className={styles.rowWrapper}>
      <button type="button" className={styles.rowSummary} onClick={() => onToggle(key)}>
        <span className={styles.rowChevron}>{isExpanded ? '▾' : '▸'}</span>
        <span className={styles.rowNumber}>Строка {row.row}</span>
        {showFileNames && row.fileName && <span className={styles.rowFile}>{row.fileName}</span>}
        <span className={styles.rowVin}>{row.vin}</span>
        <span className={styles.rowSummaryError}>
          {row.errors?.[0]}
          {row.errors?.length > 1 && ` (+${row.errors.length - 1})`}
        </span>
      </button>
      {hasOpened && (
        <div style={{ display: isExpanded ? undefined : 'none' }}>
          <div className={styles.rowOriginBar}>
            <Select
              title="Происхождение"
              placeholder="Не выбрано"
              options={originOptions}
              value={originOption}
              onChange={(val) => onOriginChange(key, val)}
            />
          </div>
          <PendingRowForm
            row={row}
            allCities={allCities}
            allMarks={allMarks}
            origin={originOption}
            onSubmitRow={onSubmitRow}
            dcCache={dcCache}
            createdModels={createdModels}
            onModelCreated={onModelCreated}
          />
        </div>
      )}
    </div>
  );
});

const StockPendingRowsTable = ({ sessionId, pendingRows, onResolved }) => {
  const [rows, setRows] = useState(pendingRows);
  const originOptions = useOriginOptions();
  const [cities, setCities] = useState([]);
  const [marks, setMarks] = useState([]);
  const [referenceLoaded, setReferenceLoaded] = useState(false);
  const [originByRow, setOriginByRow] = useState({});
  const [bulkOrigin, setBulkOrigin] = useState(null);
  // Происхождение строки: выбранное менеджером, а если не выбирал — распознанное бэком в файле
  const originOf = (row) =>
    originByRow[rowKey(row)] || originOptionFor(originOptions, row.resolvedOriginCarId, row.resolvedFactoryId);
  const [importingOriginOnly, setImportingOriginOnly] = useState(false);
  // Номера строк, для которых прямо сейчас летит одиночный commit — блокируем
  // именно эту кнопку/строку и показываем, что запрос в процессе (иначе не видно
  // ничего до ответа сервера, и можно случайно нажать "Импортировать" повторно)
  const [submittingOriginRows, setSubmittingOriginRows] = useState(() => new Set());
  const [activeTab, setActiveTab] = useState(null);
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  // Модели, добавленные пользователем прямо из формы (см. AddModelModal), на всю
  // таблицу — если модель "Soul" для KIA создали в строке 247, строка 248 с той же
  // ошибкой должна подхватить её сама, а не заставлять искать/добавлять заново.
  // Ключ: `${markId}|${название в нижнем регистре}`
  const [createdModels, setCreatedModels] = useState({});
  // Общий на всю таблицу кэш getDcList({cityId, mark}) — город+марка часто повторяются
  // в разных строках одного файла, не тянем один и тот же список по новой
  const dcCacheRef = useRef(new Map());

  // Один запрос городов + один запрос первой страницы марок на ВСЮ таблицу (не на
  // строку) — этого достаточно, чтобы сматчить то, что распозналось в файле, без
  // сетевого запроса на каждую строку
  useEffect(() => {
    Promise.all([getCities(), getMarks({ page: 1 })])
      .then(([citiesRes, marksRes]) => {
        setCities(citiesRes?.data || []);
        setMarks(marksRes?.data?.marks || []);
      })
      .finally(() => setReferenceLoaded(true));
  }, []);

  // Порядок вкладок = порядок работы: сначала комплектации (от них зависят цена по
  // приказу и оснащение, часть остальных замечаний уходит сама), потом происхождение,
  // потом остальные ошибки
  const complectationRows = useMemo(() => rows.filter((r) => r.complectationUnknown), [rows]);
  const errorRows = useMemo(() => rows.filter((r) => !r.originOnly && !r.complectationUnknown), [rows]);
  const originOnlyRows = useMemo(() => rows.filter((r) => r.originOnly && !r.complectationUnknown), [rows]);

  // Бейдж с именем файла имеет смысл, только когда строки реально пришли из
  // нескольких файлов — при загрузке одного файла это просто шум в интерфейсе
  const showFileNames = useMemo(() => new Set(rows.map((r) => r.fileName).filter(Boolean)).size > 1, [rows]);

  // По умолчанию открываем ту вкладку, где реально есть строки — при массовой
  // выгрузке из CRM это почти всегда только "нет происхождения", и незачем
  // заставлять пользователя лишний раз щёлкать
  // и переключаемся дальше, когда текущая вкладка опустела (все комплектации
  // разобраны — идём к происхождению)
  useEffect(() => {
    const counts = {
      complectations: complectationRows.length,
      origin: originOnlyRows.length,
      errors: errorRows.length,
    };
    if (activeTab !== null && counts[activeTab] > 0) return;
    const next = ['complectations', 'origin', 'errors'].find((tab) => counts[tab] > 0);
    if (next) setActiveTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complectationRows.length, originOnlyRows.length, errorRows.length]);

  const applyOriginToAll = () => {
    if (!bulkOrigin) return;
    setOriginByRow(() => {
      const next = {};
      rows.forEach((r) => {
        next[rowKey(r)] = bulkOrigin;
      });
      return next;
    });
  };

  // Стабильные ссылки на колбэки (useCallback) — обязательны для того, чтобы
  // React.memo у PendingErrorRow реально пропускал ре-рендер строк, которых
  // изменение не коснулось (иначе пропс с новой функцией на каждый рендер
  // родителя сводил бы memo на нет)
  const handleModelCreated = useCallback((markId, modelOption) => {
    if (!markId || !modelOption) return;
    const key = `${markId}|${(modelOption.label || '').toLowerCase()}`;
    setCreatedModels((prev) => ({ ...prev, [key]: modelOption }));
  }, []);

  const toggleRowExpanded = useCallback((key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleOriginChange = useCallback((key, val) => {
    setOriginByRow((prev) => ({ ...prev, [key]: val }));
  }, []);

  // Единая точка синхронизации состояния — бэк возвращает актуальный список строк,
  // ещё оставшихся в сессии (и после одиночного, и после массового коммита), так
  // что просто заменяем локальный список им, вместо ручного вычёркивания по индексу
  const applyCommitResult = useCallback(
    (data) => {
      setRows(data.pendingRows || []);
      onResolved && onResolved(data);
    },
    [onResolved]
  );

  const handleRowSubmit = useCallback(
    async (resolution) => {
      try {
        const response = await commitStockCorrections(sessionId, [resolution]);
        const data = response.data;

        if (data.imported > 0) {
          alert.success(`Строка ${resolution.row} импортирована`);
        }
        if (data.errors?.length) {
          alert.error(data.errors[0].message);
        }

        applyCommitResult(data);
      } catch (err) {
        alert.error('Ошибка при сохранении исправлений');
      }
    },
    [sessionId, applyCommitResult]
  );

  const handleComplectationCommit = useCallback(
    async (resolutions) => {
      try {
        const response = await commitStockCorrections(sessionId, resolutions);
        const data = response.data;

        if (data.imported > 0) {
          alert.success(`Импортировано строк: ${data.imported}`);
        }
        if (data.errors?.length) {
          alert.error(`Ошибок при импорте: ${data.errors.length} (см. первую: ${data.errors[0].message})`);
        }

        applyCommitResult(data);
      } catch (err) {
        alert.error('Ошибка при сохранении исправлений');
      }
    },
    [sessionId, applyCommitResult]
  );

  const handleOriginOnlySubmit = async (row) => {
    const originOption = originOf(row);
    if (!originOption) {
      alert.error('Выберите происхождение');
      return;
    }
    setSubmittingOriginRows((prev) => new Set(prev).add(rowKey(row)));
    try {
      const response = await commitStockCorrections(sessionId, [buildOriginOnlyResolution(row, originOption)]);
      const data = response.data;

      if (data.imported > 0) {
        alert.success(`Строка ${row.row} импортирована`);
      }
      if (data.errors?.length) {
        alert.error(data.errors[0].message);
      }

      applyCommitResult(data);
    } catch (err) {
      alert.error('Ошибка при сохранении исправлений');
    } finally {
      setSubmittingOriginRows((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
    }
  };

  const handleImportAllOriginOnly = async () => {
    const ready = originOnlyRows.filter((r) => originOf(r));
    if (ready.length === 0) {
      alert.error('Сначала выберите происхождение хотя бы для одной строки');
      return;
    }
    setImportingOriginOnly(true);
    try {
      const resolutions = ready.map((r) => buildOriginOnlyResolution(r, originOf(r)));
      const response = await commitStockCorrections(sessionId, resolutions);
      const data = response.data;

      if (data.imported > 0) {
        alert.success(`Импортировано строк: ${data.imported}`);
      }
      if (data.errors?.length) {
        alert.error(`Ошибок при импорте: ${data.errors.length} (см. первую: ${data.errors[0].message})`);
      }

      applyCommitResult(data);
    } catch (err) {
      alert.error('Ошибка при сохранении исправлений');
    } finally {
      setImportingOriginOnly(false);
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>Строки, требующие ручной проверки ({rows.length})</span>
        <div className={styles.bulkOrigin}>
          <Select
            title="Происхождение для всех"
            placeholder="Не выбрано"
            options={originOptions}
            value={bulkOrigin}
            onChange={setBulkOrigin}
          />
          <Button text="Применить ко всем" onClick={applyOriginToAll} disabled={!bulkOrigin} />
        </div>
      </div>

      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === 'complectations' ? styles.tabButtonActive : ''}`}
          onClick={() => setActiveTab('complectations')}
          disabled={complectationRows.length === 0}
        >
          Комплектации ({complectationRows.length})
        </button>
        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === 'origin' ? styles.tabButtonActive : ''}`}
          onClick={() => setActiveTab('origin')}
          disabled={originOnlyRows.length === 0}
        >
          Не указано происхождение ({originOnlyRows.length})
        </button>
        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === 'errors' ? styles.tabButtonActive : ''}`}
          onClick={() => setActiveTab('errors')}
          disabled={errorRows.length === 0}
        >
          Ошибки ({errorRows.length})
        </button>
      </div>

      {!referenceLoaded ? (
        <div className={styles.row}>Загрузка справочников…</div>
      ) : (
        <>
          {activeTab === 'complectations' && complectationRows.length > 0 && (
            <ComplectationQuestions rows={complectationRows} onCommit={handleComplectationCommit} />
          )}

          {activeTab === 'origin' && originOnlyRows.length > 0 && (
            <div className={styles.originOnlySection}>
              <div className={styles.originOnlyHeader}>
                <div className={styles.sectionHint}>
                  Остальные данные уже определены — укажите происхождение и импортируйте
                </div>
                <Button
                  fill
                  text={
                    importingOriginOnly
                      ? 'Импортируется…'
                      : `Импортировать все (${originOnlyRows.filter((r) => originOf(r)).length})`
                  }
                  onClick={handleImportAllOriginOnly}
                  disabled={importingOriginOnly || originOnlyRows.every((r) => !originOf(r))}
                />
              </div>
              <table className={styles.originTable}>
                <thead>
                  <tr>
                    {showFileNames && <th>Файл</th>}
                    <th>VIN</th>
                    <th>Марка / модель</th>
                    <th>Город</th>
                    <th>ДЦ</th>
                    <th>Происхождение</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {originOnlyRows.map((row) => (
                    <tr key={rowKey(row)}>
                      {showFileNames && <td className={styles.fileCell}>{row.fileName}</td>}
                      <td>{row.vin}</td>
                      <td>
                        {row.markName} {row.modelName}
                      </td>
                      <td>{row.cityName}</td>
                      <td>{row.dcName}</td>
                      <td className={styles.originTableSelectCell}>
                        <Select
                          placeholder="Не выбрано"
                          options={originOptions}
                          value={originOf(row)}
                          disabled={submittingOriginRows.has(rowKey(row))}
                          onChange={(val) => handleOriginChange(rowKey(row), val)}
                        />
                      </td>
                      <td>
                        <Button
                          fill
                          text={submittingOriginRows.has(rowKey(row)) ? 'Импортируется…' : 'Импортировать'}
                          onClick={() => handleOriginOnlySubmit(row)}
                          disabled={submittingOriginRows.has(rowKey(row))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Тоже всегда смонтировано, а не только когда вкладка активна — иначе
              переключение на "Не указано происхождение" и обратно точно так же
              сбрасывало бы формы, как и сворачивание/разворачивание строки */}
          <div style={{ display: activeTab === 'errors' ? undefined : 'none' }}>
            {errorRows.map((row) => (
              <PendingErrorRow
                key={rowKey(row)}
                row={row}
                isExpanded={expandedRows.has(rowKey(row))}
                showFileNames={showFileNames}
                originOptions={originOptions}
                originOption={originByRow[rowKey(row)] || null}
                allCities={cities}
                allMarks={marks}
                onToggle={toggleRowExpanded}
                onOriginChange={handleOriginChange}
                onSubmitRow={handleRowSubmit}
                dcCache={dcCacheRef.current}
                createdModels={createdModels}
                onModelCreated={handleModelCreated}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default StockPendingRowsTable;
